import { FunctionComponent, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { SearchInput, Table, Typography } from "@digital-services/ui";

import ErrorComponent from "@components/ErrorComponent";
import LoadingComponent from "@components/LoadingComponent";

import { ApplicationJsonModel } from "@applications/models/ApplicationJsonModel";
import { ApplicationUser } from "@applications/models/ApplicationUser";
import {
  filterApplicationUsers,
  sort,
  useGetApplicationsUsers
} from "@applications/services/ApplicationUserService";
import DeleteApplicationUserButton from "@users/applicationuser/components/DeleteApplicationUserButton";

import { useSortQueryString } from "@hooks/useSortQueryString";

import { ApplicationUserDataRow } from "./ApplicationUserDataRow";
import { ApplicationUsersHeaderRow } from "./ApplicationUsersHeaderRow";

interface Props {
  application: ApplicationJsonModel;
}

// TBD refactor
// Tech note. After expanding any Application <Card>, this component renders 4 times or 7 times
// (no matter how many Application <Card>s, and no matter how many users are in the list inside of <Card>
// TBD refactor

const ApplicationUsers: FunctionComponent<Props> = ({ application }) => {
  const { t } = useTranslation(undefined, { keyPrefix: "applications.users" });

  const [checkedAll, setCheckedAll] = useState<boolean>(false);
  const [checkedUsers, setCheckedUsers] = useState<ApplicationUser[]>([]);

  const [searchValue, setSearchValue] = useState<string>();

  const { activeSort, sortDirection, handleSort } = useSortQueryString({
    prefix: application.id
  });

  const [users, setUsers] = useState<ApplicationUser[] | undefined>();

  const { applicationUsers, isLoading, error } = useGetApplicationsUsers(
    application.id
  );

  useEffect(() => {
    // After InviteUser happens applicationUsers.length is increased +1
    // And workaround code is to to reset checkboxes AFTERWARDS.
    if (checkedUsers && checkedUsers?.length > 0) {
      setCheckedAll(false);
      setCheckedUsers([]);
    }

    const filteredUsers = filterApplicationUsers(applicationUsers, searchValue);

    const users = sort(filteredUsers, activeSort, sortDirection);
    setUsers(users);

    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    application.id,
    applicationUsers,
    searchValue,
    activeSort,
    sortDirection
  ]);

  useEffect(() => {
    if (!users || users.length === 0) return;

    const predicate = (checkedUser: ApplicationUser) =>
      users.some(u => u.userId === checkedUser.userId);

    setCheckedUsers(prev => prev.filter(predicate));
  }, [users]);

  const toggleSelectAll = () => {
    setCheckedAll(prev => {
      const next = !prev;

      if (next) {
        // YES, no currentUser logic here!
        setCheckedUsers(users!);
      } else {
        setCheckedUsers([]);
      }

      return next;
    });
  };

  const toggleRowCheckbox = (selectedAppUser: ApplicationUser) => {
    setCheckedUsers(prev => {
      const exists = prev.some(u => u.userId === selectedAppUser.userId);

      const next = exists
        ? prev.filter(u => u.userId !== selectedAppUser.userId) // uncheck
        : [...prev, selectedAppUser]; // check

      // auto‑sync checkedAll
      if (users && users.length > 0) {
        setCheckedAll(next.length === users.length); // YES, here, no need "to -1"
      }

      return next;
    });
  };

  const handleSearchInput = useCallback((newValue: string) => {
    // After start using `@digital-services/ui` when <SearchInput> is used together with useSearchQueryString() hook, then such components goes into infinite rendering.
    // In some places we can refactor to avoid useSearchQueryString() and instead use local React state.
    // But what is important, at least for May-2026, we need to implement <SearchInput> onChange handler as useCallback() instance.
    if (newValue !== searchValue) {
      setSearchValue(newValue);
    }

    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isLoading) return <LoadingComponent message={t("load.message")} />;

  if (error) return <ErrorComponent message={t("load.error.message")} />;

  return (
    <div data-testid={`applicationUsers-${application.id}`}>
      {checkedUsers.length > 0 ? (
        <div
          className="float-left flex items-center gap-1"
          data-testid="delete-users"
        >
          <Typography variant="smallTitle">
            {t("selected.message", { count: checkedUsers.length })}
          </Typography>
          <DeleteApplicationUserButton
            users={checkedUsers}
            application={application}
          />
        </div>
      ) : null}

      <div className="float-right w-[500px] pr-10 pb-5">
        <SearchInput
          data-testid={`search-application-user-${application.id}`}
          id={`search-application-user-${application.id}`}
          placeholder={t("search.placeholder")}
          onChange={handleSearchInput}
        />
      </div>

      <Table className="w-full" rowHoverHighlight>
        <thead>
          <ApplicationUsersHeaderRow
            application={application}
            activeSort={activeSort}
            sortDirection={sortDirection}
            handleSort={handleSort}
            checkedAll={checkedAll}
            toggleSelectAll={toggleSelectAll}
          />
        </thead>

        <tbody data-testid="tbody">
          {users?.map(associatedUser => (
            <ApplicationUserDataRow
              key={`applicationUsers_${application.id}_${associatedUser.userId}`}
              application={application}
              associatedUser={associatedUser}
              isChecked={checkedUsers?.some(
                u => u.userId === associatedUser.userId
              )}
              toggleRowCheckbox={toggleRowCheckbox}
            />
          ))}
        </tbody>
        <tfoot data-testid="tfoot">
          <tr>
            <td colSpan={3} className="pt-3 text-base">
              {t("count.message", { count: users?.length })}
            </td>
          </tr>
        </tfoot>
      </Table>
    </div>
  );
};

export default ApplicationUsers;
